import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, centsToUSD, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Trash2, Plus, Pin, Users, Megaphone, GraduationCap, Shirt, Loader2,
  Image as ImageIcon, Pencil, Eye, EyeOff, Type, RotateCcw, ClipboardList,
  Mail, Phone, Calendar, Sparkles, CalendarDays, Play,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { useContent } from "@/context/ContentContext";
import { EVENT_TYPES, getEventTypeMeta, formatEventRange } from "@/lib/events";

// ─── helpers ───────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-dark p-5 flex items-center gap-3" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="h-10 w-10 rounded-md grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF]">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</div>
        <div className="font-display text-2xl text-chrome">{value ?? "—"}</div>
      </div>
    </div>
  );
}

const dollarsToCents = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
};
const centsToDollarString = (c) => ((c || 0) / 100).toFixed(2);

// Generic "Save" handler — POST when no id, PUT when id is provided
async function saveItem(endpoint, id, payload) {
  if (id) return (await api.put(`${endpoint}/${id}`, payload)).data;
  return (await api.post(endpoint, payload)).data;
}

// ─── Announcements panel ────────────────────────────────────────────────────
function AnnouncementsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", pinned: false });
  const [saving, setSaving] = useState(false);

  const load = async () => setItems((await api.get("/announcements")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", body: "", pinned: false });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ title: it.title, body: it.body, pinned: !!it.pinned });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await saveItem("/announcements", editing?.id, form);
      if (!editing && result && typeof result.push_devices === "number") {
        const pd = result.push_devices;
        toast.success(pd > 0
          ? `📣 Announcement posted · pinging ${pd} device${pd === 1 ? "" : "s"}`
          : "Announcement posted · no devices have notifications on yet");
      } else {
        toast.success(editing ? "Announcement updated" : "Announcement posted");
      }
      setOpen(false);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/announcements/${id}`);
    await load(); onChange?.();
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-varsity text-2xl tracking-wider">Announcements</h2>
        <button onClick={openCreate} className="btn-electric !px-4 !py-2" data-testid="new-announcement-btn">
          <Plus size={14} /> New
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-chrome">
              {editing ? "Edit Announcement" : "New Announcement"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Title</label>
              <input className="input-line mt-1" required value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     data-testid="announcement-title-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Message</label>
              <textarea rows={5} className="input-line mt-1 resize-none" required value={form.body}
                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                        data-testid="announcement-body-input" />
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.pinned}
                     onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                     data-testid="announcement-pinned-checkbox" />
              <span className="text-sm text-white/80">Pin to top of parents&apos; feed</span>
            </label>
            <DialogFooter>
              <button type="submit" disabled={saving} className="btn-electric" data-testid="announcement-submit-btn">
                {saving ? <Loader2 className="animate-spin" size={14} /> : (editing ? "Save Changes" : "Post Announcement")}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <div className="card-dark p-8 text-center text-white/60">No announcements yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="card-dark p-5 flex items-start justify-between gap-4" data-testid={`admin-announcement-${a.id}`}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {a.pinned && <Pin size={14} className="mt-1 text-[#00E5FF] shrink-0" />}
                <div className="min-w-0">
                  <div className="font-varsity text-lg tracking-wider">{a.title}</div>
                  <p className="text-sm text-white/65 mt-1 line-clamp-2">{a.body}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(a)} className="p-2 text-white/40 hover:text-[#00E5FF]" title="Edit" data-testid={`edit-announcement-${a.id}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => remove(a.id)} className="p-2 text-white/40 hover:text-red-400" title="Delete" data-testid={`delete-announcement-${a.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tuition panel ──────────────────────────────────────────────────────────
function TuitionPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", priceDollars: "", cadence: "monthly", description: "", active: true });
  const [featuresText, setFeaturesText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => setItems((await api.get("/tuition?all=1")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", priceDollars: "", cadence: "monthly", description: "", active: true });
    setFeaturesText("");
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name,
      priceDollars: centsToDollarString(it.price_cents),
      cadence: it.cadence || "monthly",
      description: it.description || "",
      active: it.active !== false,
    });
    setFeaturesText((it.features || []).join("\n"));
    setOpen(true);
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/tuition/${it.id}`, {
        name: it.name, price_cents: it.price_cents, cadence: it.cadence,
        description: it.description || "", features: it.features || [],
        active: !(it.active !== false),
      });
      toast.success(it.active === false ? "Plan visible to parents" : "Plan hidden from parents");
      await load(); onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        price_cents: dollarsToCents(form.priceDollars),
        cadence: form.cadence,
        description: form.description,
        features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
        active: form.active,
      };
      await saveItem("/tuition", editing?.id, payload);
      toast.success(editing ? "Plan updated" : "Plan added");
      setOpen(false);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this plan? (Tip: you can hide it instead by toggling visibility)")) return;
    await api.delete(`/tuition/${id}`);
    await load(); onChange?.();
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-varsity text-2xl tracking-wider">Tuition Plans</h2>
        <button onClick={openCreate} className="btn-electric !px-4 !py-2" data-testid="new-tuition-btn">
          <Plus size={14} /> New Plan
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-chrome">
              {editing ? "Edit Plan" : "New Plan"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Name</label>
              <input className="input-line mt-1" required value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="tuition-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Price (USD)</label>
                <div className="relative">
                  <span className="absolute left-0 top-3 text-white/40">$</span>
                  <input type="text" inputMode="decimal" required value={form.priceDollars}
                         onChange={(e) => setForm({ ...form, priceDollars: e.target.value })}
                         className="input-line mt-1 pl-5" placeholder="75.00"
                         data-testid="tuition-price-input" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Cadence</label>
                <select className="input-line mt-1 bg-[#0A0A0E]" value={form.cadence}
                        onChange={(e) => setForm({ ...form, cadence: e.target.value })}
                        data-testid="tuition-cadence-select">
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                  <option value="one-time">One-time</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Description</label>
              <textarea rows={3} className="input-line mt-1 resize-none" value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="tuition-description-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Features (one per line)</label>
              <textarea rows={4} className="input-line mt-1 resize-none" value={featuresText}
                        onChange={(e) => setFeaturesText(e.target.value)}
                        data-testid="tuition-features-input" />
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active}
                     onChange={(e) => setForm({ ...form, active: e.target.checked })}
                     data-testid="tuition-active-checkbox" />
              <span className="text-sm text-white/80">Visible to parents</span>
            </label>
            <DialogFooter>
              <button type="submit" disabled={saving} className="btn-electric" data-testid="tuition-submit-btn">
                {saving ? <Loader2 className="animate-spin" size={14} /> : (editing ? "Save Changes" : "Save Plan")}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((p) => {
          const visible = p.active !== false;
          return (
            <div key={p.id} className={`card-dark p-5 flex items-start justify-between gap-3 ${visible ? "" : "opacity-60"}`} data-testid={`admin-tuition-${p.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-varsity text-xl tracking-wider">{p.name}</div>
                  {!visible && (
                    <span className="text-[10px] uppercase tracking-[0.18em] bg-white/10 text-white/60 px-2 py-0.5 rounded" data-testid={`tuition-hidden-${p.id}`}>Hidden</span>
                  )}
                </div>
                <div className="font-display text-2xl text-chrome mt-1">
                  {centsToUSD(p.price_cents)}{" "}
                  <span className="text-xs text-white/40 font-body">/ {p.cadence}</span>
                </div>
                <p className="text-sm text-white/60 mt-2 line-clamp-2">{p.description}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => toggleActive(p)} className="p-2 text-white/40 hover:text-[#00E5FF]" title={visible ? "Hide from parents" : "Show to parents"} data-testid={`toggle-tuition-${p.id}`}>
                  {visible ? <Eye size={15} /> : <EyeOff size={15} />}
                </button>
                <button onClick={() => openEdit(p)} className="p-2 text-white/40 hover:text-[#00E5FF]" title="Edit" data-testid={`edit-tuition-${p.id}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => remove(p.id)} className="p-2 text-white/40 hover:text-red-400" title="Delete" data-testid={`delete-tuition-${p.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Shop panel ─────────────────────────────────────────────────────────────
function ShopPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", priceDollars: "", category: "shirt", description: "", image_url: "", in_stock: true, active: true,
  });
  const [sizesText, setSizesText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => setItems((await api.get("/shop?all=1")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", priceDollars: "", category: "shirt", description: "", image_url: "", in_stock: true, active: true });
    setSizesText("");
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name,
      priceDollars: centsToDollarString(it.price_cents),
      category: it.category || "shirt",
      description: it.description || "",
      image_url: it.image_url || "",
      in_stock: it.in_stock !== false,
      active: it.active !== false,
    });
    setSizesText((it.sizes || []).join(", "));
    setOpen(true);
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/shop/${it.id}`, {
        name: it.name, price_cents: it.price_cents, category: it.category,
        description: it.description || "", image_url: it.image_url || null,
        sizes: it.sizes || [], in_stock: it.in_stock !== false,
        active: !(it.active !== false),
      });
      toast.success(it.active === false ? "Item visible to parents" : "Item hidden from parents");
      await load(); onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        price_cents: dollarsToCents(form.priceDollars),
        category: form.category,
        description: form.description,
        image_url: form.image_url || null,
        sizes: sizesText.split(",").map((s) => s.trim()).filter(Boolean),
        in_stock: form.in_stock,
        active: form.active,
      };
      await saveItem("/shop", editing?.id, payload);
      toast.success(editing ? "Item updated" : "Item added");
      setOpen(false);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item? (Tip: you can hide it instead)")) return;
    await api.delete(`/shop/${id}`);
    await load(); onChange?.();
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-varsity text-2xl tracking-wider">Shop Items</h2>
        <button onClick={openCreate} className="btn-electric !px-4 !py-2" data-testid="new-shop-btn">
          <Plus size={14} /> New Item
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-chrome">
              {editing ? "Edit Shop Item" : "New Shop Item"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Name</label>
              <input className="input-line mt-1" required value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="shop-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Price (USD)</label>
                <div className="relative">
                  <span className="absolute left-0 top-3 text-white/40">$</span>
                  <input type="text" inputMode="decimal" required value={form.priceDollars}
                         onChange={(e) => setForm({ ...form, priceDollars: e.target.value })}
                         className="input-line mt-1 pl-5" placeholder="25.00"
                         data-testid="shop-price-input" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Category</label>
                <select className="input-line mt-1 bg-[#0A0A0E]" value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        data-testid="shop-category-select">
                  <option value="shirt">Shirt</option>
                  <option value="uniform">Uniform</option>
                  <option value="accessory">Accessory</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Image URL</label>
              <input className="input-line mt-1" value={form.image_url}
                     onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                     placeholder="https://..."
                     data-testid="shop-image-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Description</label>
              <textarea rows={3} className="input-line mt-1 resize-none" value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="shop-description-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Sizes (comma-separated)</label>
              <input className="input-line mt-1" placeholder="YS, YM, YL, AS, AM, AL" value={sizesText}
                     onChange={(e) => setSizesText(e.target.value)}
                     data-testid="shop-sizes-input" />
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.in_stock}
                       onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
                       data-testid="shop-instock-checkbox" />
                <span className="text-sm text-white/80">In stock</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active}
                       onChange={(e) => setForm({ ...form, active: e.target.checked })}
                       data-testid="shop-active-checkbox" />
                <span className="text-sm text-white/80">Visible to parents</span>
              </label>
            </div>
            <DialogFooter>
              <button type="submit" disabled={saving} className="btn-electric" data-testid="shop-submit-btn">
                {saving ? <Loader2 className="animate-spin" size={14} /> : (editing ? "Save Changes" : "Save Item")}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const visible = it.active !== false;
          return (
            <div key={it.id} className={`card-dark p-4 ${visible ? "" : "opacity-60"}`} data-testid={`admin-shop-${it.id}`}>
              <div className="flex items-start gap-3">
                {it.image_url && (
                  <img src={it.image_url} alt="" className="h-16 w-16 object-cover rounded border border-white/10" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-varsity text-lg tracking-wider truncate">{it.name}</div>
                    {!visible && (
                      <span className="text-[9px] uppercase tracking-[0.18em] bg-white/10 text-white/60 px-1.5 py-0.5 rounded" data-testid={`shop-hidden-${it.id}`}>Hidden</span>
                    )}
                  </div>
                  <div className="text-xs text-white/40 uppercase tracking-[0.2em]">{it.category}</div>
                  <div className="font-display text-xl text-chrome mt-1">{centsToUSD(it.price_cents)}</div>
                </div>
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <button onClick={() => toggleActive(it)} className="p-1.5 text-white/40 hover:text-[#00E5FF]" title={visible ? "Hide from parents" : "Show to parents"} data-testid={`toggle-shop-${it.id}`}>
                    {visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button onClick={() => openEdit(it)} className="p-1.5 text-white/40 hover:text-[#00E5FF]" title="Edit" data-testid={`edit-shop-${it.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(it.id)} className="p-1.5 text-white/40 hover:text-red-400" title="Delete" data-testid={`delete-shop-${it.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Gallery panel ──────────────────────────────────────────────────────────
function GalleryPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ image_url: "", caption: "", sort_order: 0, media_type: "image", thumbnail_url: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => setItems((await api.get("/gallery")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ image_url: "", caption: "", sort_order: 0, media_type: "image", thumbnail_url: "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      image_url: p.image_url,
      caption: p.caption || "",
      sort_order: p.sort_order ?? 0,
      media_type: p.media_type || "image",
      thumbnail_url: p.thumbnail_url || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveItem("/gallery", editing?.id, { ...form, sort_order: Number(form.sort_order) || 0 });
      toast.success(editing ? "Item updated" : "Item added");
      setOpen(false);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/gallery/${id}`);
    await load(); onChange?.();
    toast.success("Deleted");
  };

  const isVideo = form.media_type === "video";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-varsity text-2xl tracking-wider">Team Gallery</h2>
        <button onClick={openCreate} className="btn-electric !px-4 !py-2" data-testid="new-gallery-btn">
          <Plus size={14} /> Add Photo / Video
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-chrome">
              {editing ? "Edit Item" : "Add Photo or Video"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Type</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, media_type: "image" })}
                  className={`px-3 py-2 rounded-md border text-sm font-medium tracking-wide uppercase transition-colors ${form.media_type === "image" ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "bg-transparent text-white/70 border-white/15 hover:border-white/30"}`}
                  data-testid="gallery-type-image"
                >
                  Photo
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, media_type: "video" })}
                  className={`px-3 py-2 rounded-md border text-sm font-medium tracking-wide uppercase transition-colors ${form.media_type === "video" ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "bg-transparent text-white/70 border-white/15 hover:border-white/30"}`}
                  data-testid="gallery-type-video"
                >
                  Video
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">
                {isVideo ? "Video URL" : "Image URL"}
              </label>
              <input className="input-line mt-1" required value={form.image_url}
                     onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                     placeholder={isVideo ? "https://...mp4" : "https://..."}
                     data-testid="gallery-url-input" />
              <div className="text-[11px] text-white/40 mt-2">
                {isVideo
                  ? "Paste a direct link to an .mp4 or .webm file. Upload your clip to your chat with E1 to get a hosted URL you can paste here."
                  : "Paste a direct link to a JPG/PNG. To add a new photo, upload it to your chat with E1 — you’ll get a URL you can paste here."}
              </div>
            </div>

            {isVideo && (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Thumbnail URL (optional)</label>
                <input className="input-line mt-1" value={form.thumbnail_url}
                       onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
                       placeholder="https://... (preview image shown before play)"
                       data-testid="gallery-thumb-input" />
                <div className="text-[11px] text-white/40 mt-2">
                  Optional. If blank, the first frame of the video is used as the preview.
                </div>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Caption (optional)</label>
              <input className="input-line mt-1" value={form.caption}
                     onChange={(e) => setForm({ ...form, caption: e.target.value })}
                     placeholder="State Champs 2026"
                     data-testid="gallery-caption-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Sort order (lower = first)</label>
              <input type="number" className="input-line mt-1" value={form.sort_order}
                     onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                     data-testid="gallery-sort-input" />
            </div>
            <DialogFooter>
              <button type="submit" disabled={saving} className="btn-electric" data-testid="gallery-submit-btn">
                {saving ? <Loader2 className="animate-spin" size={14} /> : (editing ? "Save Changes" : "Save Item")}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <div className="card-dark p-8 text-center text-white/60">No items yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((p) => {
            const itemIsVideo = (p.media_type === "video") || /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(p.image_url || "");
            return (
              <div key={p.id} className="card-dark overflow-hidden relative" data-testid={`admin-gallery-${p.id}`}>
                <div className="aspect-square overflow-hidden bg-black/40 relative">
                  {itemIsVideo ? (
                    p.thumbnail_url ? (
                      <img src={p.thumbnail_url} alt={p.caption || ""} className="h-full w-full object-cover" />
                    ) : (
                      <video src={p.image_url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                    )
                  ) : (
                    <img src={p.image_url} alt={p.caption || ""} className="h-full w-full object-cover" />
                  )}
                  {itemIsVideo && (
                    <div className="absolute inset-0 grid place-items-center pointer-events-none">
                      <div className="h-10 w-10 rounded-full bg-black/65 backdrop-blur grid place-items-center border border-white/30">
                        <Play size={14} className="text-white translate-x-[1px]" fill="white" />
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm bg-black/70 border border-white/15 text-white/80">
                      {itemIsVideo ? "Video" : "Photo"}
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-xs text-white/70 line-clamp-2 min-h-[2.5rem]">{p.caption || "Untitled"}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] text-white/40 uppercase tracking-[0.2em]">#{p.sort_order ?? 0}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(p)} className="text-white/40 hover:text-[#00E5FF]" title="Edit" data-testid={`edit-gallery-${p.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(p.id)} className="text-white/40 hover:text-red-400" title="Delete" data-testid={`delete-gallery-${p.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Events panel ───────────────────────────────────────────────────────────
function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
function localInputToISO(v) {
  if (!v) return null;
  try { return new Date(v).toISOString(); } catch { return null; }
}

function EventsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [includePast, setIncludePast] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", location: "", event_type: "practice",
    starts_at: "", ends_at: "", all_day: false,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const q = includePast ? "?past=1" : "";
    setItems((await api.get(`/events${q}`)).data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [includePast]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", description: "", location: "", event_type: "practice",
             starts_at: "", ends_at: "", all_day: false });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      title: it.title,
      description: it.description || "",
      location: it.location || "",
      event_type: it.event_type || "practice",
      starts_at: isoToLocalInput(it.starts_at),
      ends_at: isoToLocalInput(it.ends_at),
      all_day: !!it.all_day,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.starts_at) {
      toast.error("Please pick a start date/time");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        location: form.location,
        event_type: form.event_type,
        starts_at: localInputToISO(form.starts_at),
        ends_at: form.ends_at ? localInputToISO(form.ends_at) : null,
        all_day: form.all_day,
      };
      await saveItem("/events", editing?.id, payload);
      toast.success(editing ? "Event updated" : "Event added");
      setOpen(false);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    await load(); onChange?.();
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h2 className="font-varsity text-2xl tracking-wider">Events Calendar</h2>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-white/60 cursor-pointer">
            <input type="checkbox" checked={includePast} onChange={(e) => setIncludePast(e.target.checked)}
                   data-testid="events-include-past" />
            <span>Show past events</span>
          </label>
          <button onClick={openCreate} className="btn-electric !px-4 !py-2" data-testid="new-event-btn">
            <Plus size={14} /> New Event
          </button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-chrome">
              {editing ? "Edit Event" : "New Event"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Title</label>
              <input required className="input-line mt-1" value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     data-testid="event-title-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Type</label>
                <select value={form.event_type}
                        onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                        className="input-line mt-1 bg-[#0A0A0E]"
                        data-testid="event-type-select">
                  {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Location</label>
                <input className="input-line mt-1" value={form.location}
                       onChange={(e) => setForm({ ...form, location: e.target.value })}
                       placeholder="Main Studio, Field, etc."
                       data-testid="event-location-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Starts</label>
                <input type="datetime-local" required value={form.starts_at}
                       onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                       className="input-line mt-1"
                       data-testid="event-starts-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Ends (optional)</label>
                <input type="datetime-local" value={form.ends_at}
                       onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                       className="input-line mt-1"
                       data-testid="event-ends-input" />
              </div>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.all_day}
                     onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
                     data-testid="event-allday-checkbox" />
              <span className="text-sm text-white/80">All-day event</span>
            </label>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Description (optional)</label>
              <textarea rows={3} className="input-line mt-1 resize-none" value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="What to bring, arrival time, etc."
                        data-testid="event-description-input" />
            </div>
            <DialogFooter>
              <button type="submit" disabled={saving} className="btn-electric" data-testid="event-submit-btn">
                {saving ? <Loader2 className="animate-spin" size={14} /> : (editing ? "Save Changes" : "Save Event")}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <div className="card-dark p-8 text-center text-white/60" data-testid="admin-events-empty">No events scheduled.</div>
      ) : (
        <div className="space-y-3">
          {items.map((ev) => {
            const meta = getEventTypeMeta(ev.event_type);
            return (
              <div key={ev.id} className="card-dark p-4 sm:p-5" data-testid={`admin-event-${ev.id}`}>
                <div className="flex items-start gap-3 justify-between flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-varsity text-lg tracking-wider">{ev.title}</div>
                      <span className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-0.5 rounded-full ${meta.accent}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="text-xs text-white/60 mt-1 inline-flex items-center gap-1.5">
                      <CalendarDays size={11} /> {formatEventRange(ev.starts_at, ev.ends_at, ev.all_day)}
                    </div>
                    {ev.location && <div className="text-xs text-white/45 mt-0.5">{ev.location}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(ev)} className="p-2 text-white/40 hover:text-[#00E5FF]" title="Edit" data-testid={`edit-event-${ev.id}`}>
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => remove(ev.id)} className="p-2 text-white/40 hover:text-red-400" title="Delete" data-testid={`delete-event-${ev.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tryouts panel ──────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: "new",       label: "New",        color: "bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/30" },
  { value: "contacted", label: "Contacted",  color: "bg-amber-400/10 text-amber-300 border-amber-400/30" },
  { value: "scheduled", label: "Scheduled",  color: "bg-violet-400/10 text-violet-300 border-violet-400/30" },
  { value: "completed", label: "Completed",  color: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30" },
  { value: "declined",  label: "Declined",   color: "bg-white/10 text-white/50 border-white/20" },
];

function statusMeta(v) {
  return STATUS_OPTIONS.find((s) => s.value === v) || STATUS_OPTIONS[0];
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function TryoutDialog({ app, open, onOpenChange, onSaved }) {
  const [status, setStatus] = useState(app?.status || "new");
  const [scheduledAt, setScheduledAt] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!app) return;
    setStatus(app.status || "new");
    setNotes(app.admin_notes || "");
    if (app.scheduled_at) {
      // Convert ISO -> datetime-local
      try {
        const d = new Date(app.scheduled_at);
        const pad = (n) => String(n).padStart(2, "0");
        setScheduledAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
      } catch { setScheduledAt(""); }
    } else {
      setScheduledAt("");
    }
  }, [app]);

  if (!app) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        status,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        admin_notes: notes,
      };
      await api.put(`/tryouts/${app.id}`, payload);
      toast.success("Application updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-chrome">
            {app.dancer_name} <span className="text-white/40 text-sm font-body normal-case">· age {app.dancer_age}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm mt-2">
          <div><span className="text-white/40 text-xs uppercase tracking-[0.18em]">Parent</span><div>{app.parent_name}</div></div>
          <div><span className="text-white/40 text-xs uppercase tracking-[0.18em]">Submitted</span><div>{fmtDate(app.created_at)}</div></div>
          <div className="flex items-center gap-2"><Mail size={13} className="text-white/40" /><a href={`mailto:${app.parent_email}`} className="text-[#00E5FF] hover:underline">{app.parent_email}</a></div>
          <div className="flex items-center gap-2"><Phone size={13} className="text-white/40" /><a href={`tel:${app.parent_phone}`} className="text-[#00E5FF] hover:underline">{app.parent_phone}</a></div>
          {app.grade && <div><span className="text-white/40 text-xs uppercase tracking-[0.18em]">School/Grade</span><div>{app.grade}</div></div>}
          <div><span className="text-white/40 text-xs uppercase tracking-[0.18em]">Experience</span><div>{app.experience_years} year{app.experience_years === 1 ? "" : "s"}</div></div>
        </div>

        {Array.isArray(app.styles) && app.styles.length > 0 && (
          <div className="mt-4">
            <div className="text-white/40 text-xs uppercase tracking-[0.18em] mb-1">Styles done</div>
            <div className="flex flex-wrap gap-1.5">
              {app.styles.map((s) => (
                <span key={s} className="text-[11px] uppercase tracking-[0.15em] border border-white/15 text-white/75 px-2 py-0.5 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        )}

        {app.availability && (
          <div className="mt-4"><div className="text-white/40 text-xs uppercase tracking-[0.18em] mb-1">Availability</div><div className="text-sm text-white/80 whitespace-pre-wrap">{app.availability}</div></div>
        )}
        {app.why_join && (
          <div className="mt-4"><div className="text-white/40 text-xs uppercase tracking-[0.18em] mb-1">Why join?</div><div className="text-sm text-white/80 whitespace-pre-wrap">{app.why_join}</div></div>
        )}
        {app.referral && (
          <div className="mt-4"><div className="text-white/40 text-xs uppercase tracking-[0.18em] mb-1">Heard about us via</div><div className="text-sm text-white/80">{app.referral}</div></div>
        )}
        {app.medical_notes && (
          <div className="mt-4"><div className="text-white/40 text-xs uppercase tracking-[0.18em] mb-1">Medical notes</div><div className="text-sm text-white/80">{app.medical_notes}</div></div>
        )}
        {app.video_url && (
          <div className="mt-4">
            <div className="text-white/40 text-xs uppercase tracking-[0.18em] mb-1">Showcase video</div>
            <a
              href={app.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#00E5FF] hover:underline break-all"
              data-testid={`tryout-video-link-${app.id}`}
            >
              <Play size={13} fill="currentColor" /> {app.video_url}
            </a>
          </div>
        )}
        <div className="mt-4 text-xs text-white/40">
          Photo consent: {app.photo_consent ? <span className="text-emerald-300">Yes</span> : <span className="text-amber-300">No</span>}
        </div>

        <form onSubmit={save} className="mt-8 space-y-4 border-t border-white/10 pt-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                      className="input-line mt-1 bg-[#0A0A0E]"
                      data-testid="tryout-status-select">
                {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Tryout date/time</label>
              <input type="datetime-local" value={scheduledAt}
                     onChange={(e) => setScheduledAt(e.target.value)}
                     className="input-line mt-1"
                     data-testid="tryout-schedule-input" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/50">Coach notes (private)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="input-line mt-1 resize-none"
                      placeholder="Hot prospect, schedule alongside her cousin, etc."
                      data-testid="tryout-notes-input" />
          </div>
          <DialogFooter>
            <button type="submit" disabled={saving} className="btn-electric" data-testid="tryout-save-btn">
              {saving ? <Loader2 className="animate-spin" size={14} /> : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TryoutsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      setItems((await api.get("/tryouts")).data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this application? This can't be undone.")) return;
    await api.delete(`/tryouts/${id}`);
    await load(); onChange?.();
    toast.success("Deleted");
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s.value] = items.filter((i) => i.status === s.value).length;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <ClipboardList size={18} className="text-[#00E5FF]" />
        <h2 className="font-varsity text-2xl tracking-wider">Tryout Applications</h2>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="tryout-filter-group">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] rounded-full border ${
            filter === "all" ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:border-[#00E5FF]/40"
          }`}
          data-testid="tryout-filter-all"
        >
          All <span className="ml-1 text-white/50">({items.length})</span>
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] rounded-full border ${
              filter === s.value ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:border-[#00E5FF]/40"
            }`}
            data-testid={`tryout-filter-${s.value}`}
          >
            {s.label} <span className="ml-1 text-white/50">({counts[s.value] || 0})</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card-dark p-10 text-center text-white/55" data-testid="tryouts-empty">
          <Sparkles className="mx-auto mb-3 text-[#00E5FF]" />
          No applications {filter === "all" ? "yet" : `with status "${filter}"`}.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((app) => {
            const meta = statusMeta(app.status);
            return (
              <div key={app.id} className="card-dark p-4 sm:p-5" data-testid={`tryout-row-${app.id}`}>
                <div className="flex flex-wrap items-start gap-3 justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-varsity text-lg tracking-wider">{app.dancer_name}</div>
                      <span className="text-xs text-white/50">age {app.dancer_age}</span>
                      <span className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-0.5 rounded-full ${meta.color}`}>
                        {meta.label}
                      </span>
                      {app.video_url && (
                        <span className="text-[10px] uppercase tracking-[0.18em] border border-[#00E5FF]/40 text-[#00E5FF] px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`tryout-has-video-${app.id}`}>
                          <Play size={9} fill="currentColor" /> Video
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-white/55 mt-1">
                      Parent: <span className="text-white/80">{app.parent_name}</span> · {app.parent_email} · {app.parent_phone}
                    </div>
                    {app.scheduled_at && (
                      <div className="text-xs text-violet-300 mt-1 inline-flex items-center gap-1">
                        <Calendar size={11} /> Tryout: {fmtDate(app.scheduled_at)}
                      </div>
                    )}
                    {Array.isArray(app.styles) && app.styles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {app.styles.slice(0, 4).map((s) => (
                          <span key={s} className="text-[10px] uppercase tracking-[0.15em] border border-white/15 text-white/70 px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                        {app.styles.length > 4 && <span className="text-[10px] text-white/40">+{app.styles.length - 4}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => { setActive(app); setOpen(true); }} className="btn-ghost !px-3 !py-1.5 !text-[10px]" data-testid={`tryout-open-${app.id}`}>
                      Review
                    </button>
                    <button onClick={() => remove(app.id)} className="p-2 text-white/40 hover:text-red-400" data-testid={`tryout-delete-${app.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TryoutDialog
        app={active}
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setActive(null); }}
        onSaved={() => { load(); onChange?.(); }}
      />
    </div>
  );
}

// ─── Content (editable copy) panel ──────────────────────────────────────────
const CONTENT_GROUPS = [
  {
    label: "Home — Hero",
    keys: [
      ["landing.hero.overline", "Overline (small label above heading)"],
      ["landing.hero.headline_top", "Headline — top line (chrome)"],
      ["landing.hero.headline_accent", "Headline — accent word (blue)"],
      ["landing.hero.subtitle", "Sub-headline paragraph", "textarea"],
      ["landing.hero.cta_primary", "Primary button text"],
      ["landing.hero.cta_secondary", "Secondary button text"],
    ],
  },
  {
    label: "Home — Hero Featured Video",
    keys: [
      ["landing.hero.video_url", "Hero video URL (mp4/webm). Leave blank to use the photo background."],
      ["landing.hero.video_poster", "Hero video poster image URL (shown before video loads, optional)"],
    ],
  },
  {
    label: "Home — Features Section",
    keys: [
      ["landing.features.overline", "Overline"],
      ["landing.features.title", "Section title"],
      ["landing.features.card1.title", "Card 1 — title"],
      ["landing.features.card1.body", "Card 1 — body", "textarea"],
      ["landing.features.card2.title", "Card 2 — title"],
      ["landing.features.card2.body", "Card 2 — body", "textarea"],
      ["landing.features.card3.title", "Card 3 — title"],
      ["landing.features.card3.body", "Card 3 — body", "textarea"],
      ["landing.features.card4.title", "Card 4 — title"],
      ["landing.features.card4.body", "Card 4 — body", "textarea"],
    ],
  },
  {
    label: "Home — Gallery Strip",
    keys: [
      ["landing.gallery.large.overline", "Large tile overline"],
      ["landing.gallery.large.title", "Large tile title"],
      ["landing.gallery.small.overline", "Small tile overline"],
      ["landing.gallery.small.title", "Small tile title"],
    ],
  },
  {
    label: "Home — Tuition Teaser + CTA",
    keys: [
      ["landing.tuition.overline", "Tuition section overline"],
      ["landing.tuition.title", "Tuition section title"],
      ["landing.cta.title", "Bottom CTA — title"],
      ["landing.cta.body", "Bottom CTA — body", "textarea"],
      ["landing.cta.primary", "Bottom CTA — primary button"],
      ["landing.cta.secondary", "Bottom CTA — secondary button"],
    ],
  },
  {
    label: "Tuition Page",
    keys: [
      ["tuition.page.overline", "Overline"],
      ["tuition.page.title", "Title"],
      ["tuition.page.subtitle", "Subtitle", "textarea"],
      ["tuition.page.faq_overline", "FAQ overline"],
      ["tuition.page.faq_body", "FAQ body", "textarea"],
    ],
  },
  {
    label: "Shop Page",
    keys: [
      ["shop.page.overline", "Overline"],
      ["shop.page.title", "Title"],
      ["shop.page.subtitle", "Subtitle", "textarea"],
    ],
  },
  {
    label: "Gallery Page",
    keys: [
      ["gallery.page.overline", "Overline"],
      ["gallery.page.title", "Title"],
      ["gallery.page.subtitle", "Subtitle", "textarea"],
    ],
  },
  {
    label: "Events Page",
    keys: [
      ["events.page.overline", "Overline"],
      ["events.page.title", "Title"],
      ["events.page.subtitle", "Subtitle", "textarea"],
      ["landing.events.overline", "Home — events teaser overline"],
      ["landing.events.title", "Home — events teaser title"],
    ],
  },
  {
    label: "Join / Tryout Page",
    keys: [
      ["join.page.overline", "Overline"],
      ["join.page.title", "Title"],
      ["join.page.subtitle", "Subtitle", "textarea"],
      ["join.step1.title", "Section 1 title (Family)"],
      ["join.step2.title", "Section 2 title (Dancer)"],
      ["join.step3.title", "Section 3 title (Final)"],
      ["join.success.title", "After-submit headline"],
      ["join.success.body", "After-submit body", "textarea"],
    ],
  },
  {
    label: "Footer",
    keys: [
      ["footer.tagline", "Footer tagline under team name"],
    ],
  },
];

function ContentPanel() {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    setDraft({ ...content });
  }, [content]);

  const setVal = (key, v) => setDraft((d) => ({ ...d, [key]: v }));

  const save = async (key) => {
    setSavingKey(key);
    try {
      await api.put(`/content/${encodeURIComponent(key)}`, { value: draft[key] ?? "" });
      toast.success("Saved");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const reset = async (key) => {
    if (!window.confirm("Reset this text to the original wording?")) return;
    setSavingKey(key);
    try {
      const { data } = await api.post(`/content/${encodeURIComponent(key)}/reset`);
      toast.success("Reset to default");
      setVal(key, data.value);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <Type size={18} className="text-[#00E5FF]" />
        <h2 className="font-varsity text-2xl tracking-wider">Site Wording</h2>
      </div>
      <p className="text-sm text-white/55 mb-8 max-w-2xl">
        Every piece of public-facing wording on the site is editable here. Edit the field, hit <span className="text-white">Save</span>,
        and your change goes live the next time anyone loads that page. Use <span className="text-white">Reset</span> to revert to the original copy.
      </p>

      <div className="space-y-8">
        {CONTENT_GROUPS.map((group) => (
          <section key={group.label} className="card-dark p-5" data-testid={`content-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="overline mb-4">{group.label}</div>
            <div className="space-y-5">
              {group.keys.map(([key, label, kind]) => {
                const value = draft[key] ?? "";
                const original = content[key] ?? "";
                const dirty = value !== original;
                const busy = savingKey === key;
                return (
                  <div key={key}>
                    <label className="block text-xs uppercase tracking-[0.18em] text-white/55 mb-1">{label}</label>
                    {kind === "textarea" ? (
                      <textarea
                        rows={3}
                        value={value}
                        onChange={(e) => setVal(key, e.target.value)}
                        className="w-full bg-white/5 border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-md px-3 py-2 text-white resize-none"
                        data-testid={`content-input-${key}`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => setVal(key, e.target.value)}
                        className="w-full bg-white/5 border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-md px-3 py-2 text-white"
                        data-testid={`content-input-${key}`}
                      />
                    )}
                    <div className="mt-2 flex items-center gap-2 justify-end">
                      <button
                        onClick={() => reset(key)}
                        className="text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-[#00E5FF] inline-flex items-center gap-1"
                        data-testid={`content-reset-${key}`}
                        disabled={busy}
                      >
                        <RotateCcw size={11} /> Reset
                      </button>
                      <button
                        onClick={() => save(key)}
                        disabled={!dirty || busy}
                        className="btn-electric !px-3 !py-1.5 !text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`content-save-${key}`}
                      >
                        {busy ? <Loader2 className="animate-spin" size={11} /> : "Save"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

// ─── Parents panel ──────────────────────────────────────────────────────────
function ParentsPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/parents").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  return (
    <div>
      <h2 className="font-varsity text-2xl tracking-wider mb-5">Registered Parents</h2>
      <div className="card-dark overflow-x-auto" data-testid="parents-table">
        <table className="w-full text-sm">
          <thead className="text-left text-white/50">
            <tr className="border-b border-white/10">
              <th className="px-4 py-3 uppercase tracking-[0.2em] text-xs">Name</th>
              <th className="px-4 py-3 uppercase tracking-[0.2em] text-xs">Email</th>
              <th className="px-4 py-3 uppercase tracking-[0.2em] text-xs">Dancer</th>
              <th className="px-4 py-3 uppercase tracking-[0.2em] text-xs">Phone</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-6 text-white/50 text-center">No parents registered yet.</td></tr>
            ) : items.map((u) => (
              <tr key={u.id} className="border-b border-white/5" data-testid={`parent-row-${u.id}`}>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 text-white/70">{u.email}</td>
                <td className="px-4 py-3 text-white/70">{u.dancer_name || "—"}</td>
                <td className="px-4 py-3 text-white/70">{u.phone || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Admin Page ────────────────────────────────────────────────────────
export default function Admin() {
  const [stats, setStats] = useState({});

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" richColors />
      <main className="mx-auto max-w-7xl w-full px-6 py-12 flex-1">
        <div className="overline mb-3">Admin Console</div>
        <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">Coach Dashboard</h1>
        <p className="mt-3 text-white/65 max-w-xl">Post announcements, edit tuition prices, manage the shop, and see who&apos;s signed up.</p>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard icon={Users} label="Parents" value={stats.parents} />
          <StatCard icon={ClipboardList} label="New Tryouts" value={stats.tryouts_new} />
          <StatCard icon={Calendar} label="Events" value={stats.events_upcoming} />
          <StatCard icon={Megaphone} label="Announcements" value={stats.announcements} />
          <StatCard icon={GraduationCap} label="Plans" value={stats.tuition_plans} />
          <StatCard icon={Shirt} label="Shop Items" value={stats.shop_items} />
        </div>

        <Tabs defaultValue="announcements" className="mt-12">
          <TabsList className="bg-white/5 border border-white/10 flex-wrap">
            <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="tryouts" data-testid="tab-tryouts">Tryouts</TabsTrigger>
            <TabsTrigger value="tuition" data-testid="tab-tuition">Tuition</TabsTrigger>
            <TabsTrigger value="shop" data-testid="tab-shop">Shop</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">Wording</TabsTrigger>
            <TabsTrigger value="parents" data-testid="tab-parents">Parents</TabsTrigger>
          </TabsList>
          <TabsContent value="announcements" className="mt-8"><AnnouncementsPanel onChange={loadStats} /></TabsContent>
          <TabsContent value="events" className="mt-8"><EventsPanel onChange={loadStats} /></TabsContent>
          <TabsContent value="tryouts" className="mt-8"><TryoutsPanel onChange={loadStats} /></TabsContent>
          <TabsContent value="tuition" className="mt-8"><TuitionPanel onChange={loadStats} /></TabsContent>
          <TabsContent value="shop" className="mt-8"><ShopPanel onChange={loadStats} /></TabsContent>
          <TabsContent value="gallery" className="mt-8"><GalleryPanel onChange={loadStats} /></TabsContent>
          <TabsContent value="content" className="mt-8"><ContentPanel /></TabsContent>
          <TabsContent value="parents" className="mt-8"><ParentsPanel /></TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
